import { Button, Tooltip, Typography } from 'antd';
import React from 'react';

import type { ListingToolbarActionItem } from './listingToolbarConfig';

/** Aria-label when `label` is a plain string (icon-only / a11y). */
export function listingActionAriaLabel(
  label: React.ReactNode,
): string | undefined {
  return typeof label === 'string' ? label : undefined;
}

type ListingActionTestIdProps = {
  id: string;
  /** Canonical on header actions; also accepted on toolbar items. */
  testId?: string;
  /** Canonical on toolbar actions; also accepted on header items. */
  dataTestId?: string;
};

/**
 * Resolves `data-testid` from either prop name.
 * Header falls back to `rm-header-action-{id}` when neither is set.
 */
export function resolveListingActionTestId(
  item: ListingActionTestIdProps,
  options?: { headerFallback?: boolean },
): string | undefined {
  const explicit = item.testId ?? item.dataTestId;
  if (explicit != null) return explicit;
  if (options?.headerFallback) return `rm-header-action-${item.id}`;
  return undefined;
}

/** Wraps a control in Tooltip when `tooltip` is set. */
export function wrapListingActionTooltip(
  key: string,
  control: React.ReactNode,
  tooltip?: string,
): React.ReactNode {
  if (!tooltip) {
    return <React.Fragment key={key}>{control}</React.Fragment>;
  }
  return (
    <Tooltip key={key} title={tooltip}>
      {control}
    </Tooltip>
  );
}

function renderActionControl(
  item: ListingToolbarActionItem,
  options?: { headerFallback?: boolean },
): React.ReactNode {
  const testId = resolveListingActionTestId(item, options);
  if (item.kind === 'custom') {
    return item.render();
  }
  if (item.kind === 'caption') {
    return (
      <Typography.Text
        type={item.type ?? 'secondary'}
        strong={item.strong}
        data-testid={testId}
      >
        {item.label}
      </Typography.Text>
    );
  }
  if (item.kind === 'link') {
    return (
      <Button
        type="link"
        size="small"
        onClick={item.onClick}
        disabled={item.disabled}
        data-testid={testId}
      >
        {item.label}
      </Button>
    );
  }
  return (
    <Button
      type={item.type}
      color={item.color}
      variant={item.variant}
      danger={item.danger}
      size={item.size}
      loading={item.loading}
      icon={item.icon}
      aria-label={item.icon ? listingActionAriaLabel(item.label) : undefined}
      onClick={item.onClick}
      disabled={item.disabled}
      data-testid={testId}
    >
      {item.label}
    </Button>
  );
}

/** Renders a toolbar/header action, including optional Tooltip wrap. */
export function renderListingToolbarActionItem(
  item: ListingToolbarActionItem,
  options?: { headerFallback?: boolean },
): React.ReactNode {
  const control = renderActionControl(item, options);
  const tooltip = item.kind === 'custom' ? undefined : item.tooltip;
  return wrapListingActionTooltip(item.id, control, tooltip);
}
